import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient'; // Import singleton


interface SupabaseContextType {
  salonId: string | null;
  supabase: typeof supabase;
  isLoading: boolean;
  error: string | null;

  /**
   * Salon geo / money metadata
   * - countryCode is ISO-2 (e.g. CZ)
   * - currencyCode is ISO-3 (e.g. EUR)
   */
  countryCode: string | null;
  currencyCode: string;
  refreshSalonMeta: () => Promise<void>;

  refreshSession: () => Promise<void>;
  retryConnection: () => Promise<void>;
  retryWithBackoff: <T>(
    operation: () => Promise<T>,
    maxRetries?: number,
    baseDelay?: number
  ) => Promise<T>;
}

export const SupabaseContext = createContext<SupabaseContextType>({
  salonId: null,
  supabase: supabase,
  isLoading: false,
  error: null,

  countryCode: null,
  currencyCode: 'EUR',
  refreshSalonMeta: async () => {},

  refreshSession: async () => {},
  retryConnection: async () => {},
  retryWithBackoff: async () => { throw new Error('Not implemented'); },
});

export function SupabaseProvider({ children }: { children: React.ReactNode }) {
  const [salonId, setSalonId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [currencyCode, setCurrencyCode] = useState<string>('EUR');

  const lastSessionUserId = useRef<string | null>(null);

  /**
   * Safe function to set salon ID with type checking
   */
  function safeSetSalonId(val: any) {
    if (typeof val === 'string') {
      setSalonId(val);
    } else if (val === null || val === undefined) {
      setSalonId(null);
    } else {
      setSalonId('');
    }
  }

  /**
   * Sleep utility for retry delays
   */
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Retry operation with exponential backoff
   * Enhanced with better error handling and rate limit detection
   */
  const retryWithBackoff = async <T,>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    let lastError: Error;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (err) {
        lastError = err as Error;
        const errorMessage = err instanceof Error ? err.message : String(err);
        
        // Detect different types of errors for appropriate retry delays
        const isRateLimit =
          errorMessage.includes('Request rate limit reached') ||
          errorMessage.includes('over_request_rate_limit') ||
          errorMessage.includes('rate limit') ||
          errorMessage.includes('429');
          
        const isNetworkError =
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('fetch') ||
          errorMessage.includes('network') ||
          errorMessage.includes('ECONNRESET') ||
          errorMessage.includes('ETIMEDOUT');
          
        if (attempt < maxRetries - 1) {
          let delay;
          if (isRateLimit) {
            // Longer delay for rate limits with exponential backoff
            delay = Math.min(baseDelay * Math.pow(3, attempt + 2), 60000);
          } else if (isNetworkError) {
            // Moderate delay for network errors
            delay = Math.min(baseDelay * Math.pow(1.5, attempt), 10000);
          } else {
            // Standard exponential backoff for other errors
            delay = Math.min(baseDelay * Math.pow(2, attempt), 15000);
          }
          await sleep(delay);
        }
      }
    }
    throw lastError!;
  };

  const enforceActiveAccount = useCallback(async (userId: string): Promise<boolean> => {
    const { data, error: statusError } = await supabase
      .from('salon_users')
      .select('status')
      .eq('user_id', userId)
      .maybeSingle();

    if (statusError && statusError.code !== 'PGRST116') {
      // If we can't read status for some reason, don't hard-block here.
      return true;
    }

    const normalizedStatus = String((data as any)?.status || '').trim().toLowerCase();
    if (normalizedStatus && normalizedStatus !== 'active') {
      await supabase.auth.signOut();
      safeSetSalonId(null);
      setError('Your account has been deactivated. Please contact your salon admin.');
      return false;
    }
    return true;
  }, [safeSetSalonId]);

  /**
   * Fetch salon ID from database
   * IMPORTANT: This function only READS data, it never creates salon_users records
   * All salon_users creation is handled by the Edge Function during registration
   */
  const fetchSalonId = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network and try again.');
      }

      // Get current session using singleton client
      const sessionResult = await retryWithBackoff(async () => {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
        return session;
      }, 2, 3000);

      if (!sessionResult) {
        safeSetSalonId(null);
        setIsLoading(false);
        return;
      }

      const isActive = await enforceActiveAccount(sessionResult.user.id);
      if (!isActive) {
        setIsLoading(false);
        return;
      }

      // Try to get existing salon_user record (READ ONLY)
      const salonUser = await retryWithBackoff(async () => {
        const { data, error: salonUserError } = await supabase
          .from('salon_users')
          .select('id, salon_id, status')
          .eq('user_id', sessionResult.user.id)
          .maybeSingle();
        
        if (salonUserError) {
          console.error('Salon user fetch error:', salonUserError);
          throw new Error(`Failed to fetch salon user: ${salonUserError.message}`);
        }
        return data;
      }, 2, 2000);

      const normalizedStatus = String((salonUser as any)?.status || '').trim().toLowerCase();
      if (salonUser && normalizedStatus && normalizedStatus !== 'active') {
        await supabase.auth.signOut();
        safeSetSalonId(null);
        setError('Your account has been deactivated. Please contact your salon admin.');
        setIsLoading(false);
        return;
      }

      if (salonUser) {
        // Found existing salon_users record
        safeSetSalonId(salonUser.salon_id);
        console.log('✅ Found existing salon_users record:', salonUser.salon_id);
      } else {
        // Try to find salon by email (READ ONLY)
        const salon = await retryWithBackoff(async () => {
          const { data, error: salonByEmailError } = await supabase
            .from('salons')
            .select('id')
            .eq('email', sessionResult.user.email)
            .maybeSingle();
          
          if (salonByEmailError) {
            console.error('Salon by email fetch error:', salonByEmailError);
            throw new Error(`Failed to fetch salon by email: ${salonByEmailError.message}`);
          }
          return data;
        }, 2, 2000);

        if (salon) {
          // Found salon by email, but no salon_users record
          // This means user needs to complete registration via Edge Function
          console.log('⚠️ Found salon by email but no salon_users record - registration may be incomplete');
          safeSetSalonId(salon.id);
          
          // NOTE: We DO NOT create salon_users records here anymore!
          // This is now handled by the Edge Function during registration
          // If the user reaches this state, they should be redirected to complete registration
        } else {
          // No salon found - user needs to register
          safeSetSalonId(''); // Empty string indicates need to register
          console.log('ℹ️ No salon found for user - needs registration');
        }
      }
    } catch (err) {
      console.error('Error in fetchSalonId:', err);
      safeSetSalonId(null);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch salon metadata (countryCode, currencyCode) from database
   * This is used to standardize currency formatting and other locale-specific settings
   */
  const fetchSalonMeta = useCallback(async () => {
    try {
      if (!salonId) return;

      const { data, error: salonError } = await supabase
        .from('salons')
        .select('country_code, currency_code, settings')
        .eq('id', salonId)
        .maybeSingle();

      if (salonError) throw salonError;

      const fromCountryColumn = (data as any)?.country_code;
      const fromCountrySettings = (data as any)?.settings?.country;
      const nextCountry = String(fromCountryColumn || fromCountrySettings || '').trim().toUpperCase();
      setCountryCode(/^[A-Z]{2}$/.test(nextCountry) ? nextCountry : null);

      const fromCurrencyColumn = (data as any)?.currency_code;
      const fromCurrencySettings = (data as any)?.settings?.currency;
      const nextCurrency = String(fromCurrencyColumn || fromCurrencySettings || 'EUR').trim().toUpperCase();
      setCurrencyCode(/^[A-Z]{3}$/.test(nextCurrency) ? nextCurrency : 'EUR');
    } catch {
      // Safe defaults
      setCountryCode(null);
      setCurrencyCode('EUR');
    }
  }, [salonId]);

  /**
   * Refresh salon metadata - wrapper to call fetchSalonMeta
   */
  const refreshSalonMeta = useCallback(async () => {
    await fetchSalonMeta();
  }, [fetchSalonMeta]);

  /**
   * Effect to fetch salon ID and metadata on component mount
   */
  useEffect(() => {
    // Fetch salon ID on component mount
    fetchSalonId();

    // Subscribe to authentication state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      
      // Only fetch if the user ID has changed (prevents unnecessary calls)
      if (userId !== lastSessionUserId.current) {
        lastSessionUserId.current = userId;
        if (session) {
          fetchSalonId();
        } else {
          // User signed out
          safeSetSalonId(null);
          setError(null);
        }
      }
    });

    // Handle online/offline events
    const handleOnline = () => {
      if (error && error.includes('internet connection')) {
        fetchSalonId();
      }
    };
    
    const handleOffline = () => {
      setError('No internet connection. Please check your network and try again.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup subscriptions
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line
  }, []);

  /**
   * Effect to fetch salon metadata when salonId changes
   */
  useEffect(() => {
    if (!salonId) {
      setCountryCode(null);
      setCurrencyCode('EUR');
      return;
    }
    fetchSalonMeta();
  }, [salonId, fetchSalonMeta]);

  /**
   * Refresh session manually
   * This function only refreshes the session and re-fetches salon data
   * It does NOT create any salon_users records
   */
  const refreshSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network and try again.');
      }
      
      // Refresh the authentication session
      const sessionData = await retryWithBackoff(async () => {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw new Error(`Session refresh failed: ${error.message}`);
        return data;
      }, 2, 3000);

      if (sessionData.session) {
        const isActive = await enforceActiveAccount(sessionData.session.user.id);
        if (!isActive) {
          setIsLoading(false);
          return;
        }

        // Try to find existing salon_users record (READ ONLY)
        const salonUser = await retryWithBackoff(async () => {
          const { data, error } = await supabase
            .from('salon_users')
            .select('id, salon_id, status')
            .eq('user_id', sessionData.session.user.id)
            .maybeSingle();
          if (error) throw new Error(`Failed to fetch salon user: ${error.message}`);
          return data;
        }, 2, 2000);

        const normalizedStatus = String((salonUser as any)?.status || '').trim().toLowerCase();
        if (salonUser && normalizedStatus && normalizedStatus !== 'active') {
          await supabase.auth.signOut();
          safeSetSalonId(null);
          setError('Your account has been deactivated. Please contact your salon admin.');
          setIsLoading(false);
          return;
        }

        if (salonUser) {
          safeSetSalonId(salonUser.salon_id);
          console.log('✅ Session refreshed - found salon_users record');
        } else {
          // Try to find salon by email (READ ONLY)
          const salon = await retryWithBackoff(async () => {
            const { data, error: salonError } = await supabase
              .from('salons')
              .select('id')
              .eq('email', sessionData.session.user.email)
              .maybeSingle();
            if (salonError && salonError.code !== 'PGRST116') {
              throw new Error(`Failed to fetch salon by email: ${salonError.message}`);
            }
            return data;
          }, 2, 2000);

          if (salon) {
            safeSetSalonId(salon.id);
            console.log('✅ Session refreshed - found salon by email');
            
            // NOTE: We DO NOT create salon_users records here!
            // This is handled by the Edge Function during registration
          } else {
            safeSetSalonId('');
            console.log('ℹ️ Session refreshed - no salon found');
          }
        }
      } else {
        safeSetSalonId(null);
        console.log('ℹ️ Session refreshed - no active session');
      }
    } catch (err) {
      safeSetSalonId(null);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      console.error('Error refreshing session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Retry connection (alias for refreshSession)
   */
  const retryConnection = async () => {
    setError(null);
    await fetchSalonId();
  };

  /**
   * Enforce deactivated-user logout even without an auth state change.
   * Runs on window focus and periodically.
   */
  useEffect(() => {
    let mounted = true;

    const check = async () => {
      if (!mounted) return;
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      await enforceActiveAccount(userId);
    };

    const onFocus = () => {
      check();
    };

    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(() => {
      check();
    }, 5 * 60 * 1000);

    check();

    return () => {
      mounted = false;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, [enforceActiveAccount]);

  // Context value with all necessary functions and state
  const value = {
    salonId,
    supabase, // Use singleton
    isLoading,
    error,

    countryCode,
    currencyCode,
    refreshSalonMeta,

    refreshSession,
    retryConnection,
    retryWithBackoff,
  };

  return (
    <SupabaseContext.Provider value={value}>{children}</SupabaseContext.Provider>
  );
}

export const useSupabase = () => useContext(SupabaseContext);